import { PurLookupOption } from "react-lib/apis/PUR/purLookupsApi";
import { PurDraftCreateInput } from "react-lib/apis/PUR/purDraftCreatePilot";
import { PurOrderLine, PurRecord, PurRequestLine } from "react-lib/apis/PUR/purApi";
import { readId } from "react-lib/apis/PUR/purRead";
import { PurRequestOrderInput } from "react-lib/apis/PUR/purRequestOrder";
import type { PurReceiptInput } from "react-lib/apis/PUR/purReceiptCreate";
import { buildPurDraftCreateData, buildPurReceiptPayloadData, PurPayloadValidationError } from "react-lib/apis/PUR/purPayloadValidation";

export interface DraftIssue { id: string; line?: number }
export interface PurchaseLineDraft {
  key: number; product: PurLookupOption; unit: PurLookupOption | null;
  quantity: string; factor: string; unitCost: string; discount?: string;
  sourceRequestItemId?: number;
}
export interface PurchaseRequestSource {
  id: number; number: string; editedAt: string; projectId: number; storageId: number;
  lines: Array<{ id: number; productId: number; unitId: number | null; factor: string; remaining: string }>;
}
export interface PurchaseDraft {
  kind: "materialRequests" | "purchaseOrders"; date: string;
  project: PurLookupOption | null; vendor: PurLookupOption | null; storage: PurLookupOption | null;
  purpose: string; notes: string; lines: PurchaseLineDraft[];
  deliveryDate: string; discount: string; vatRate: string;
  sourceRequest?: PurchaseRequestSource;
}
export interface ReceiptLineDraft {
  key: number; source: PurOrderLine; selected: boolean;
  hasLots: boolean | null;
  delivered: string; accepted: string; rejected: string; reason: string;
  factor: string; unitCost: string; batch: string; manufactured: string; expires: string;
}
export interface ReceiptDraft {
  po: PurRecord | null; date: string; deliveryNumber: string;
  storage: PurLookupOption | null; rejectedStorage: PurLookupOption | null; notes: string;
  totalIncludingVat: string;
  lines: ReceiptLineDraft[];
}

export const localDate = (): string => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
export const emptyPurchaseDraft = (kind: PurchaseDraft["kind"]): PurchaseDraft => ({
  kind, date: localDate(), project: null, vendor: null, storage: null, purpose: "", notes: "", lines: [], deliveryDate: "", discount: "", vatRate: "",
});
export const emptyReceiptDraft = (): ReceiptDraft => ({
  po: null, date: localDate(), deliveryNumber: "", storage: null, rejectedStorage: null, notes: "", totalIncludingVat: "", lines: [],
});

// Integer arithmetic avoids 0.1 + 0.2 errors and never silently rounds stock.
// Reject values outside JS's exact integer range instead of accepting a lossy preview.
export const decimalUnits = (text: string, places = 2): number | null => {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!match) return null;
  const fraction = match[2] || "";
  if (fraction.slice(places).replace(/0/g, "")) return null;
  const value = Number(`${match[1]}${fraction.slice(0, places).padEnd(places, "0")}`);
  return Number.isSafeInteger(value) ? value : null;
};
export const formatUnits = (value: number, places = 2): string => {
  if (!Number.isSafeInteger(value)) return "—";
  const digits = String(Math.abs(value)).padStart(places + 1, "0");
  return `${value < 0 ? "-" : ""}${digits.slice(0, -places)}.${digits.slice(-places)}`;
};

export const requestOrderPreparationIssue = (record: PurRecord, divisionId: number | string | null | undefined): string | null => {
  if (!readId(record.id) || !["APPROVED", "PARTIALLY_ORDERED"].includes(record.status)
    || readId(divisionId) === null || record.requestingDivisionId !== readId(divisionId)
    || !record.projectId || readId(record.storageId) === null || !record.items.length) return "MEERP_MR_PO_SOURCE_ERROR";
  const seen = new Set<number>();
  let hasRemaining = false;
  for (const item of record.items) {
    if (item.kind !== "materialRequests" || item.id === null || item.productId === null
      || item.requestId !== record.id || seen.has(item.id)) return "MEERP_MR_PO_SOURCE_ERROR";
    seen.add(item.id);
    const requested = decimalUnits(item.requestedQuantity || ""), ordered = decimalUnits(item.orderedQuantity || "");
    if (requested === null || ordered === null || ordered > requested) return "MEERP_MR_PO_SOURCE_ERROR";
    if (requested > ordered) hasRemaining = true;
  }
  return hasRemaining ? null : "MEERP_MR_PO_NO_REMAINING";
};

// Options reflect document snapshots, not a claim that a master is still active.
const requestSnapshotOption = (id: number, label: string): PurLookupOption => ({
  id, label: label || `#${id}`, code: "", unitId: null, unitName: "", hasLots: null, cost: null, salePrice: null, productType: "",
});
export const orderDraftFromRequest = (record: PurRecord, divisionId: number | string | null | undefined): PurchaseDraft => {
  const issue = requestOrderPreparationIssue(record, divisionId);
  if (issue) throw new Error(issue);
  const items = record.items.filter((line): line is PurRequestLine => line.kind === "materialRequests")
    .filter((line) => decimalUnits(line.requestedQuantity!)! > decimalUnits(line.orderedQuantity!)!);
  const lines = items.map((line) => ({
    id: line.id!, productId: line.productId!, unitId: line.unitId, factor: line.conversionFactor || "",
    remaining: formatUnits(decimalUnits(line.requestedQuantity!)! - decimalUnits(line.orderedQuantity!)!),
  }));
  return {
    ...emptyPurchaseDraft("purchaseOrders"),
    project: requestSnapshotOption(record.projectId!, record.projectNumber),
    storage: requestSnapshotOption(readId(record.storageId)!, record.storageName),
    purpose: record.purpose,
    sourceRequest: { id: record.id, number: record.number, editedAt: record.editedAt,
      projectId: record.projectId!, storageId: readId(record.storageId)!, lines },
    lines: items.map((line, index) => ({
      key: index + 1, sourceRequestItemId: line.id!,
      product: requestSnapshotOption(line.productId!, line.productName),
      unit: line.unitId === null ? null : requestSnapshotOption(line.unitId, line.unitName),
      quantity: lines[index].remaining, factor: lines[index].factor, unitCost: "", discount: "",
    })),
  };
};
export const baseQuantity = (quantity: string, factor: string): string => {
  const qty = decimalUnits(quantity), multiplier = decimalUnits(factor, 4);
  if (qty === null || multiplier === null) return "—";
  return formatUnits(qty * multiplier, 6);
};
export const remainingQuantity = (line: PurOrderLine): string => {
  const ordered = decimalUnits(line.orderedQuantity || ""), received = decimalUnits(line.receivedQuantity || "");
  return ordered === null || received === null ? "—" : formatUnits(ordered - received);
};
export const validDate = (text: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const date = new Date(`${text}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === text;
};
const positive = (text: string, places = 2): boolean => {
  const value = decimalUnits(text, places);
  return value !== null && value > 0;
};

export const validatePurchaseDraft = (draft: PurchaseDraft): DraftIssue[] => {
  const issues: DraftIssue[] = [];
  if (!validDate(draft.date)) issues.push({ id: "MEERP_DRAFT_DATE_ERROR" });
  if (draft.deliveryDate && !validDate(draft.deliveryDate)) issues.push({ id: "MEERP_DRAFT_DATE_ERROR" });
  const validOptionalAmount = (text: string | undefined): boolean => !text || decimalUnits(text) !== null;
  const validOptionalRate = (text: string | undefined): boolean => validOptionalAmount(text) && (!text || (decimalUnits(text) || 0) <= 10000);
  if (draft.kind === "purchaseOrders" && (!validOptionalAmount(draft.discount) || !validOptionalRate(draft.vatRate))) issues.push({ id: "MEERP_DRAFT_TAX_INPUT_ERROR" });
  // Review and CREATE share payload rules. Pilot scope/permissions stay in
  // validatePurchaseCreate; legacy MR batches do not accept these fields.
  if (draft.kind === "purchaseOrders" && !draft.sourceRequest) {
    if (!draft.vatRate.trim()) issues.push({ id: "MEERP_CREATE_VAT_REQUIRED" });
    if (draft.discount.trim() && decimalUnits(draft.discount) !== 0) issues.push({ id: "MEERP_CREATE_HEADER_DISCOUNT_ERROR" });
  }
  if (!draft.project || !draft.storage || (draft.kind === "purchaseOrders" && !draft.vendor)) issues.push({ id: "MEERP_DRAFT_HEADER_ERROR" });
  if (draft.kind === "materialRequests" && !draft.purpose.trim()) issues.push({ id: "MEERP_DRAFT_PURPOSE_ERROR" });
  if (!draft.lines.length) issues.push({ id: "MEERP_DRAFT_LINES_ERROR" });
  if (draft.sourceRequest && (draft.kind !== "purchaseOrders" || draft.project?.id !== draft.sourceRequest.projectId
    || draft.storage?.id !== draft.sourceRequest.storageId)) issues.push({ id: "MEERP_MR_PO_SOURCE_ERROR" });
  const seen = new Set<number>();
  const seenSource = new Set<number>();
  draft.lines.forEach((item, index) => {
    const line = index + 1;
    if (seen.has(item.product.id)) issues.push({ id: "MEERP_DRAFT_DUPLICATE_ERROR", line });
    seen.add(item.product.id);
    if (!item.unit || !positive(item.factor, 4)) issues.push({ id: "MEERP_DRAFT_UNIT_ERROR", line });
    if (!positive(item.quantity)) issues.push({ id: "MEERP_DRAFT_QUANTITY_ERROR", line });
    if (draft.kind === "purchaseOrders" && decimalUnits(item.unitCost) === null) issues.push({ id: "MEERP_DRAFT_COST_ERROR", line });
    if (draft.kind === "purchaseOrders" && !validOptionalAmount(item.discount)) issues.push({ id: "MEERP_DRAFT_TAX_INPUT_ERROR", line });
    if (baseQuantity(item.quantity, item.factor) === "—") issues.push({ id: "MEERP_DRAFT_PRECISION_ERROR", line });
    if (draft.sourceRequest) {
      const source = draft.sourceRequest.lines.find((entry) => entry.id === item.sourceRequestItemId);
      if (!source || seenSource.has(source.id) || source.productId !== item.product.id || source.unitId !== item.unit?.id
        || source.factor !== item.factor) issues.push({ id: "MEERP_MR_PO_SOURCE_ERROR", line });
      if (source) {
        seenSource.add(source.id);
        const quantity = decimalUnits(item.quantity), remaining = decimalUnits(source.remaining);
        if (quantity === null || remaining === null || quantity > remaining) issues.push({ id: "MEERP_MR_PO_REMAINING_ERROR", line });
      }
    }
  });
  if (!issues.length && !draft.sourceRequest) {
    try { buildPurDraftCreateData(purchasePayloadInput(draft)); }
    catch (error) {
      issues.push(error instanceof PurPayloadValidationError
        ? { id: error.messageId, ...(error.line ? { line: error.line } : {}) }
        : { id: "MEERP_CREATE_VALIDATION_ERROR" });
    }
  }
  return issues;
};

export const validatePurchaseCreate = (draft: PurchaseDraft): DraftIssue[] => {
  const issues = validatePurchaseDraft(draft);
  // Ordinary PO CREATE cannot atomically link MR lines or update ordered_qty.
  if (draft.sourceRequest || draft.lines.some((line) => line.sourceRequestItemId !== undefined)) issues.push({ id: "MEERP_MR_PO_LINKED_SAVE_BLOCKED" });
  return issues;
};

export const validateRequestOrderDraft = (draft: PurchaseDraft): DraftIssue[] => {
  // Legacy API takes its price from the master. A blank comparison price is
  // valid here; this synthetic zero is only used for validation, never sent.
  return validatePurchaseDraft({ ...draft, lines: draft.lines.map((line) => ({ ...line, unitCost: line.unitCost.trim() || "0" })) });
};
export const requestOrderInput = (draft: PurchaseDraft): PurRequestOrderInput => {
  const issues = validateRequestOrderDraft(draft);
  if (draft.kind !== "purchaseOrders" || !draft.sourceRequest || issues.length) throw new Error(issues[0]?.id || "MEERP_MR_PO_SOURCE_ERROR");
  return {
    mrId: draft.sourceRequest.id, editedAt: draft.sourceRequest.editedAt, projectId: draft.project!.id, storageId: draft.storage!.id, vendorId: draft.vendor!.id,
    lines: draft.lines.map((line) => ({ requestItemId: line.sourceRequestItemId!, productId: line.product.id, unitId: line.unit!.id,
      factor: line.factor, remaining: draft.sourceRequest!.lines.find((source) => source.id === line.sourceRequestItemId)!.remaining,
      quantity: line.quantity, unitCost: line.unitCost })),
  };
};

const purchasePayloadInput = (draft: PurchaseDraft): PurDraftCreateInput => {
  // MR has no header notes; PO has no purpose field. Preserve both visible
  // fields in the supported text field, and disclose this mapping in the UI.
  const text = draft.kind === "materialRequests"
    ? [draft.purpose.trim(), draft.notes.trim() ? `[Notes]\n${draft.notes.trim()}` : ""].filter(Boolean).join("\n")
    : [draft.purpose.trim() ? `[Purpose]\n${draft.purpose.trim()}` : "", draft.notes.trim()].filter(Boolean).join("\n");
  return {
    kind: draft.kind, projectId: draft.project!.id, storageId: draft.storage!.id, vendorId: draft.vendor?.id,
    date: draft.date, deliveryDate: draft.deliveryDate, text, vatRate: draft.vatRate.trim(),
    lines: draft.lines.map((line) => ({ productId: line.product.id, unitId: line.unit!.id,
      quantity: line.quantity.trim(), factor: line.factor.trim(), unitCost: line.unitCost.trim(),
      discount: line.discount?.trim() || "0" })),
  };
};

export const purchaseCreateInput = (draft: PurchaseDraft): PurDraftCreateInput => {
  if (validatePurchaseCreate(draft).length) throw new Error("Invalid PUR create draft");
  return purchasePayloadInput(draft);
};

export const receiptDraftFromOrder = (po: PurRecord): ReceiptDraft => ({
  ...emptyReceiptDraft(), po,
  // Header IDs are not assumed to be valid choices in the current lookup/scope.
  lines: po.items.filter((line): line is PurOrderLine => line.kind === "purchaseOrders").map((source, index) => ({
    key: index + 1, source, hasLots: source.hasLots ?? null, selected: false, delivered: "", accepted: "", rejected: "0", reason: "",
    factor: source.conversionFactor || "", unitCost: source.unitCost || "", batch: "", manufactured: "", expires: "",
  })),
});

export const validateReceiptDraft = (draft: ReceiptDraft): DraftIssue[] => {
  const issues: DraftIssue[] = [];
  if (!draft.po || !["APPROVED", "PARTIALLY_RECEIVED", "TO_RECEIVE"].includes(draft.po.status)) issues.push({ id: "MEERP_DRAFT_PO_ERROR" });
  if (!validDate(draft.date)) issues.push({ id: "MEERP_DRAFT_DATE_ERROR" });
  if (!draft.storage) issues.push({ id: "MEERP_DRAFT_RECEIPT_HEADER_ERROR" });
  if (draft.totalIncludingVat.trim() && decimalUnits(draft.totalIncludingVat) === null) issues.push({ id: "MEERP_DRAFT_RECEIPT_AMOUNT_ERROR" });
  if (!draft.lines.some((line) => line.selected)) issues.push({ id: "MEERP_DRAFT_LINES_ERROR" });
  const totals = new Map<number, number>();
  const batches = new Map<string, string>();
  draft.lines.forEach((item, index) => {
    if (!item.selected) return;
    const line = index + 1;
    const delivered = decimalUnits(item.delivered), accepted = decimalUnits(item.accepted), rejected = decimalUnits(item.rejected);
    if (!item.source.id || !item.source.productId || !item.source.unitId) issues.push({ id: "MEERP_DRAFT_SOURCE_ERROR", line });
    if (delivered === null || delivered <= 0 || accepted === null || rejected === null || !Number.isSafeInteger((accepted || 0) + (rejected || 0)) || delivered !== (accepted || 0) + (rejected || 0)) issues.push({ id: "MEERP_DRAFT_BALANCE_ERROR", line });
    if (!positive(item.factor, 4) || decimalUnits(item.unitCost) === null) issues.push({ id: "MEERP_DRAFT_UNIT_COST_ERROR", line });
    const goodBase = baseQuantity(item.accepted, item.factor), badBase = baseQuantity(item.rejected, item.factor);
    // Current receive service writes integer Stock quantities. Fractional results
    // must be resolved by the backend policy, never rounded by the form.
    if (goodBase === "—" || badBase === "—" || !goodBase.endsWith(".000000") || !badBase.endsWith(".000000")) issues.push({ id: "MEERP_DRAFT_STOCK_INTEGER_ERROR", line });
    if ((rejected || 0) > 0 && (!item.reason.trim() || !draft.rejectedStorage)) issues.push({ id: "MEERP_DRAFT_REJECT_ERROR", line });
    if (typeof item.hasLots !== "boolean" || item.hasLots !== item.source.hasLots) issues.push({ id: "MEERP_RECEIPT_PRODUCT_ERROR", line });
    if (item.hasLots === true) {
      if (!item.batch.trim() || !validDate(item.manufactured) || !validDate(item.expires) || item.manufactured > item.expires || item.manufactured > draft.date || ((accepted || 0) > 0 && item.expires <= draft.date)) issues.push({ id: "MEERP_DRAFT_LOT_ERROR", line });
      const batchKey = `${item.source.productId}:${item.batch.trim()}`;
      const dates = `${item.manufactured}:${item.expires}`;
      if (batches.has(batchKey) && batches.get(batchKey) !== dates) issues.push({ id: "MEERP_DRAFT_LOT_CONFLICT_ERROR", line });
      batches.set(batchKey, dates);
    } else if (item.manufactured || item.expires) issues.push({ id: "MEERP_DRAFT_NON_LOT_DATES_ERROR", line });
    if (item.source.id && accepted !== null) totals.set(item.source.id, (totals.get(item.source.id) || 0) + accepted);
  });
  totals.forEach((total, id) => {
    const source = draft.lines.find((line) => line.source.id === id)?.source;
    const remaining = source ? decimalUnits(remainingQuantity(source)) : null;
    if (remaining === null || !Number.isSafeInteger(total) || total > remaining) issues.push({ id: "MEERP_DRAFT_REMAINING_ERROR" });
  });
  if (!issues.length) {
    try { buildPurReceiptPayloadData(receiptPayloadInput(draft)); }
    catch (error) {
      const selected = draft.lines.map((item, index) => ({ item, line: index + 1 })).filter(({ item }) => item.selected);
      issues.push(error instanceof PurPayloadValidationError
        ? { id: error.messageId, ...(error.line ? { line: selected[error.line - 1].line } : {}) }
        : { id: "MEERP_RECEIPT_DATA_ERROR" });
    }
  }
  return issues;
};

const receiptPayloadInput = (draft: ReceiptDraft): PurReceiptInput => {
  return { po: draft.po!, storageId: draft.storage!.id, rejectedStorageId: draft.rejectedStorage?.id || null, date: draft.date,
    metadata: { deliveryNumber: draft.deliveryNumber.trim(), totalIncludingVat: draft.totalIncludingVat.trim(), notes: draft.notes.trim() },
    items: draft.lines.filter((line) => line.selected).map((line) => ({ poItemId: line.source.id!, productId: line.source.productId!, unitId: line.source.unitId!,
      hasLots: line.hasLots!,
      delivered: line.delivered.trim(), accepted: line.accepted.trim(), rejected: line.rejected.trim(), factor: line.factor.trim(), unitCost: line.unitCost.trim(),
      reason: line.reason.trim(), batch: line.batch.trim(), manufactured: line.manufactured, expires: line.expires })) };
};

export const receiptCreateInput = (draft: ReceiptDraft): PurReceiptInput => {
  const issues = validateReceiptDraft(draft);
  if (issues.length) throw new Error(issues[0].id);
  return receiptPayloadInput(draft);
};
